import React, { type SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import { G300, N300 } from '@atlaskit/adf-schema';
import Button, { ButtonGroup } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import SuccessIcon from '@atlaskit/icon/glyph/check-circle';
import { Radio } from '@atlaskit/radio';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import PageHeader from '@atlassian/jira-common-components-page-header';
import {
	GenericPermissionErrorPageAsync,
	JSErrorPageAsync,
	NotFoundErrorPageAsync,
} from '@atlassian/jira-error-pages/src/async.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { FlagGroup, SuccessAutoDismissFlag } from '@atlassian/jira-flags';
import HelpPanelButton from '@atlassian/jira-help-panel-button';
import { useIntl } from '@atlassian/jira-intl';
import { setApplicationProperties } from '@atlassian/jira-platform-application-properties/src/services/rest-v3/index.tsx';
import {
	ContextualAnalyticsData,
	fireOperationalAnalytics,
	FireScreenAnalytics,
	fireUIAnalytics,
	SCREEN,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useCompassProductSettingsResource } from '@atlassian/jira-router-resources-compass-product-settings/src';
import { NEW_COMPASS_PROJECTS_ENABLED_KEY } from '@atlassian/jira-router-resources-compass-product-settings/src/constants';
import { SOFTWARE } from '@atlassian/jira-shared-types/src/application-key.tsx';
import { PageLayout } from '@atlassian/jira-spa-apps-page-layout/src/page/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { messages } from './messages';

const Settings = {
	Compass: 'compass',
	Jira: 'jira',
} as const;

type SettingsKey = (typeof Settings)[keyof typeof Settings];

const CompassProductSettingsPage = () => {
	const { formatMessage } = useIntl();
	const tenantContext = useTenantContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data, loading, error: dataRetrievalError } = useCompassProductSettingsResource();

	const [saveInProgress, setSaveInProgress] = useState(false);
	const [value, setValue] = useState<SettingsKey>();
	const [initialValue, setInitialValue] = useState<SettingsKey>();
	const [flags, setFlags] = useState<Array<number>>([]);

	const getInitialValue = useCallback(
		() => (data?.isCompassEnabledForNewProjects ? Settings.Compass : Settings.Jira),
		[data],
	);

	useEffect(() => {
		if (data) {
			if (data?.isCompassEnabledForNewProjects !== undefined) {
				setInitialValue(getInitialValue());
			}
		}
	}, [data, getInitialValue]);

	useEffect(() => {
		if (initialValue) {
			setValue(initialValue);
		}
	}, [initialValue]);

	const handleDismiss = () => {
		setFlags(flags.slice(1));
	};

	const addFlag = useCallback(() => {
		const newFlagId = flags.length + 1;
		const newFlags = flags.slice();
		newFlags.splice(0, 0, newFlagId);
		setFlags(newFlags);
	}, [flags]);

	const handleChange = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		({ currentTarget }: SyntheticEvent<any>) => {
			setValue(currentTarget?.value);
		},
		[setValue],
	);

	const handleDiscardChanges = useCallback(() => {
		if (initialValue) setValue(initialValue);
	}, [initialValue]);

	const handleSave = useCallback(async () => {
		setSaveInProgress(true);

		const newData = {
			id: NEW_COMPASS_PROJECTS_ENABLED_KEY,
			value: value === Settings.Compass,
		};
		await setApplicationProperties('', NEW_COMPASS_PROJECTS_ENABLED_KEY, JSON.stringify(newData))
			.then(() => {
				addFlag();
				setInitialValue(value);
				setSaveInProgress(false);
				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'jiraSpaAppsCompassProductSettings.setNewCompassProjectsApplicationProperty succeeded',
					{ defaultSettingForNewProjects: value },
				);
			})
			.catch((error) => {
				setSaveInProgress(false);
				fireErrorAnalytics({
					meta: {
						id: 'setNewCompassProjectsApplicationProperty',
						packageName: 'jiraSpaAppsCompassProductSettings',
					},
					sendToPrivacyUnsafeSplunk: true,
					error,
				});
			});
	}, [addFlag, createAnalyticsEvent, value]);

	if (!tenantContext.isSiteAdmin) {
		return <GenericPermissionErrorPageAsync />;
	}

	if (!tenantContext.licensedProducts[SOFTWARE]) {
		return <NotFoundErrorPageAsync />;
	}

	if (dataRetrievalError) {
		return <JSErrorPageAsync />;
	}

	return (
		<ContextualAnalyticsData sourceName="compassProductSettingsPage" sourceType={SCREEN}>
			<PageLayout>
				<PageHeader>{formatMessage(messages.pageHeader)}</PageHeader>
				<PageSubheader>
					<Description data-testid="spa-apps-compass-product-settings.ui.page-subheader">
						{formatMessage(messages.pageSubheaderSpork)}
					</Description>
					<HelpPanelButton
						articleId="vnkvMTEMH9pGdm5r9sTtl"
						appearance="link"
						spacing="none"
						fallbackComponent={
							<a
								href="https://support.atlassian.com/jira-software-cloud/docs/what-are-compass-components/"
								data-testid="spa-apps-compass-product-settings.ui.a"
								target="_blank"
							>
								{formatMessage(messages.pageSubheaderSporkLink)}
							</a>
						}
					>
						{formatMessage(messages.pageSubheaderSporkLink)}
					</HelpPanelButton>
				</PageSubheader>
				{loading ? (
					<DefaultsLoading>
						<Spinner size="large" />
					</DefaultsLoading>
				) : (
					<>
						<Section>
							<Heading as="h2" size="xsmall">
								{formatMessage(messages.projectDefaultSettingTitle)}
							</Heading>
							<Option>
								<Radio
									isChecked={value === Settings.Compass}
									onChange={handleChange}
									name={Settings.Compass}
									value={Settings.Compass}
									label={formatMessage(messages.projectDefaultSettingCompassOption)}
								/>
								<OptionDescriptionWrapper>
									<OptionDescription>
										{formatMessage(messages.projectDefaultSettingCompassOptionDescription)}
									</OptionDescription>
								</OptionDescriptionWrapper>
							</Option>
							<Option>
								<Radio
									isChecked={value === Settings.Jira}
									onChange={handleChange}
									name={Settings.Jira}
									value={Settings.Jira}
									label={formatMessage(messages.projectDefaultSettingJiraOption)}
								/>
								<OptionDescriptionWrapper>
									<OptionDescription>
										{formatMessage(messages.projectDefaultSettingJiraOptionDescription)}
									</OptionDescription>
								</OptionDescriptionWrapper>
							</Option>
						</Section>
						<ButtonGroup>
							<Button
								type="submit"
								appearance="primary"
								isDisabled={value === initialValue || saveInProgress}
								onClick={(_, analyticsEvent) => {
									fireUIAnalytics(
										analyticsEvent,
										'button clicked',
										'compassProductSettingsSaveButton',
										{ defaultSettingForNewProjects: value },
									);
									handleSave();
								}}
							>
								{formatMessage(messages.projectDefaultSettingSave)}
							</Button>
							<Button
								type="reset"
								appearance="default"
								isDisabled={value === initialValue || saveInProgress}
								onClick={(_, analyticsEvent) => {
									fireUIAnalytics(
										analyticsEvent,
										'button clicked',
										'compassProductSettingsDiscardChangesButton',
									);
									handleDiscardChanges();
								}}
							>
								{formatMessage(messages.projectDefaultSettingDiscardChanges)}
							</Button>
						</ButtonGroup>
					</>
				)}
				<FlagGroup onDismissed={handleDismiss}>
					{flags.map((flagId) => (
						<SuccessAutoDismissFlag
							id={flagId}
							key={flagId}
							icon={
								<SuccessIcon
									primaryColor={token('color.icon.success', G300)}
									label="Success"
									size="medium"
								/>
							}
							title={formatMessage(messages.changesSavedSuccessFlag)}
						/>
					))}
				</FlagGroup>
			</PageLayout>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

export default CompassProductSettingsPage;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DefaultsLoading = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	maxWidth: '500px',
	height: '150px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.p({
	marginRight: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageSubheader = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', N300),
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	flexWrap: 'wrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionDescription = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', N300),
	marginLeft: token('space.400', '28px'),
	font: token('font.body.small'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionDescriptionWrapper = styled.div({
	marginLeft: token('space.negative.050', '-4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Option = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	paddingTop: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Section = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	marginTop: token('space.300', '24px'),
	marginBottom: token('space.300', '24px'),
	alignSelf: 'stretch',
});
